import { Fragment, useEffect, useMemo, type ReactNode } from "react";
import { useAllEvents, type EventCard as EventCardType } from "../lib/api/useEventsList";
import { CarouselRail } from "./EventCarouselRail";
import { getEventType, CAROUSEL_THEMES, themeForTypes } from "@shared/eventTypes";

/**
 * The "category landing" view of the events directory — rendered by EventsIndex
 * whenever one or more type filters are active (`/events?t=drag`).
 *
 * Events are bucketed into the CURATED carousel themes (CAROUSEL_THEMES — the
 * same branded rows as the home feed: "The Main Character", "Last Call", "Pour
 * Decisions"…) via themeForTypes(), so the filtered view shows curated titles,
 * never generic group names. Anything that matches no theme falls into a single
 * "More events" row, so nothing in the city is hidden. Reuses the real
 * EventCard (real cover flyers) and the real list endpoint.
 */

// Auto-load enough pages that theme bucketing sees the city's real inventory
// (24 events/page). Comprehensive coverage for very large cities is a
// server-side follow-up; this covers typical cities without a manual "load more".
const MAX_AUTO_PAGES = 12;

// A themed row needs at least this many events to stand on its own; anything
// thinner folds into the "More events" catch-all so the filtered view never
// shows a 1-3 event sliver row.
const MIN_ROW = 4;

// A tag is a "crowd" anchor if it's a community/identity tag — but pride behaves
// like a theme (activity), so it's excluded. Used only to narrow multi-tag
// selections (AND across crowd vs activity, OR within a role).
const isCrowd = (slug: string) =>
  getEventType(slug)?.group === "community" && slug !== "pride";

export function CategoryLanding({
  types,
  cityId,
  cityName,
  dateFrom,
  dateTo,
  excludedTypes,
  kikiSlot,
}: {
  types: string[];
  cityId: string | null;
  cityName: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  excludedTypes: string[];
  kikiSlot?: ReactNode;
}) {
  const q = useAllEvents({ types, city: cityId, dateFrom, dateTo });
  const { data: qData, hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  // Auto-load a few pages so theme bucketing has enough to work with. Deps are
  // the specific stable values — NOT the whole `q` object, which gets a new
  // reference every render and would fire fetchNextPage in a loop.
  const loadedPages = qData?.pages.length ?? 0;
  useEffect(() => {
    if (hasNextPage && loadedPages < MAX_AUTO_PAGES && !isFetchingNextPage)
      fetchNextPage();
  }, [loadedPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const events = useMemo<EventCardType[]>(() => {
    const flat = (q.data?.pages ?? []).flatMap((p) =>
      p.success ? p.data?.items ?? [] : [],
    );
    // Drop past / excluded, then dedupe by BOTH link and name+date. Same link =
    // same event (collapses paginated overlap); same name+date collapses
    // double-entered listings with different ids. Recurring instances on
    // different dates keep distinct name+date keys, so they stay separate.
    const seenLink = new Set<string>();
    const seenND = new Set<string>();
    const out: EventCardType[] = [];
    for (const e of flat) {
      if (e.isPast || e.types.some((t) => excludedTypes.includes(t))) continue;
      const link = e.slug ?? e.link;
      const nd = e.name ? `${e.name.trim().toLowerCase()}|${e.date}` : null;
      if (seenLink.has(link) || (nd && seenND.has(nd))) continue;
      seenLink.add(link);
      if (nd) seenND.add(nd);
      out.push(e);
    }
    return out;
  }, [q.data, excludedTypes]);

  // Role-aware narrowing for multi-tag: AND across roles, OR within a role.
  // The list endpoint returns a loose superset for multi-tag (gay+outdoors also
  // returns gay-only events), so we narrow here:
  //   gay + outdoors        → gay AND outdoors   (different roles → AND)
  //   pottery + nightlife   → pottery OR nightlife (same role → OR)
  const joined = useMemo(() => {
    if (types.length <= 1) return events;
    const crowdSel = types.filter(isCrowd);
    const actSel = types.filter((t) => !isCrowd(t));
    return events.filter(
      (e) =>
        (!crowdSel.length || crowdSel.some((t) => e.types.includes(t))) &&
        (!actSel.length || actSel.some((t) => e.types.includes(t))),
    );
  }, [events, types]);

  // Bucket events into curated themes — first match in CAROUSEL_THEMES order, so
  // every event lands in exactly one branded row. Leftovers (no theme match)
  // collect in a single "More events" row so nothing is hidden. Rows are sorted
  // by size so the fullest curated rows lead.
  const shelves = useMemo(() => {
    const byTheme = new Map<string, EventCardType[]>();
    const leftover: EventCardType[] = [];
    const seen = new Set<string>();
    for (const e of joined) {
      if (seen.has(e.link)) continue; // link-unique → no duplicate React keys
      seen.add(e.link);
      const theme = themeForTypes(e.types);
      if (theme) {
        const arr = byTheme.get(theme.id);
        if (arr) arr.push(e);
        else byTheme.set(theme.id, [e]);
      } else {
        leftover.push(e);
      }
    }
    const byDate = (a: EventCardType, b: EventCardType) =>
      a.date.localeCompare(b.date);
    const themed: { id: string; title: string; items: EventCardType[] }[] = [];
    for (const t of CAROUSEL_THEMES) {
      const items = byTheme.get(t.id);
      if (!items) continue;
      // Thin buckets fold into the catch-all instead of showing a sliver row.
      if (items.length < MIN_ROW) {
        leftover.push(...items);
        continue;
      }
      themed.push({ id: `theme-${t.id}`, title: t.title, items: items.sort(byDate) });
    }
    themed.sort((a, b) => b.items.length - a.items.length);
    if (leftover.length)
      themed.push({ id: "more", title: "more events", items: leftover.sort(byDate) });
    return themed;
  }, [joined]);

  if (q.isLoading) {
    return (
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 py-16 text-center text-base-content/50">
        Loading…
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto py-8 space-y-10">
        {!shelves.length && (
          <p className="px-4 sm:px-6 text-center text-base-content/50 py-16">
            No events here yet for {cityName ?? "this city"} — try another city or
            fewer filters.
          </p>
        )}
        {shelves.map((shelf, i) => (
          <Fragment key={shelf.id}>
            <div className="space-y-10">
              <CarouselRail
                carousel={{
                  id: shelf.id,
                  title: shelf.title,
                  largeCards: false,
                  items: shelf.items,
                }}
                compact
              />
            </div>
            {i === 0 && kikiSlot}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
